// Labels shared by the Teams, Divisions and Jobs screens (contracts/phase3.md).

import { ordinal } from "@lumen/team-emblem";
import type { DivisionRef, TeamEvent, TeamPolicy, TeamZone, WeekOutcome, WeekStatus } from "../api/schemas";

export { ordinal };

export const POLICY_LABEL: Record<TeamPolicy, string> = { invite_only: "Invite only", open_link: "Open link", public: "Public" };

/** "Division 2 · 1" (tier · index); "—" before placement. */
export function divisionText(d: DivisionRef | null | undefined): string {
  if (!d) return "—";
  return d.index === null ? `Division ${d.tier}` : `Division ${d.tier} · ${d.index}`;
}

/** Anchor id of a division table on the Divisions page. */
export const divisionAnchor = (tier: number, index: number | null) => `d${tier}-${index ?? 1}`;

export const ZONE_LABEL: Record<TeamZone, string> = { promotion: "promotion", safe: "safe", relegation: "relegation" };
export const ZONE_TONE: Record<TeamZone, string> = { promotion: "teams", safe: "muted", relegation: "danger" };

export const OUTCOME_LABEL: Record<WeekOutcome, string> = { promoted: "promoted", stayed: "stayed", relegated: "relegated", dormant: "went dormant" };
export const OUTCOME_TONE: Record<WeekOutcome, string> = { promoted: "teams", stayed: "muted", relegated: "danger", dormant: "warning" };

export const WEEK_STATUS_LABEL: Record<WeekStatus, string> = { open: "in progress", pending: "ended · not closed", closed: "closed" };
export const WEEK_STATUS_TONE: Record<WeekStatus, string> = { open: "info", pending: "warning", closed: "muted" };

/** "2026-W39" → Monday–Sunday in UTC, e.g. "22–28 Sep". */
export function weekRange(week: string): string {
  const m = /^(\d{4})-W(\d{2})$/.exec(week);
  if (!m) return week;
  const year = Number(m[1]);
  const w = Number(m[2]);
  // ISO week 1 contains 4 January; its Monday is the start.
  const jan4 = Date.UTC(year, 0, 4);
  const dow = (new Date(jan4).getUTCDay() + 6) % 7;
  const monday = new Date(jan4 - dow * 86_400_000 + (w - 1) * 7 * 86_400_000);
  const sunday = new Date(monday.getTime() + 6 * 86_400_000);
  const day = new Intl.DateTimeFormat("en-GB", { day: "numeric", timeZone: "UTC" });
  const dayMonth = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  return monday.getUTCMonth() === sunday.getUTCMonth() ? `${day.format(monday)}–${dayMonth.format(sunday)}` : `${dayMonth.format(monday)} – ${dayMonth.format(sunday)}`;
}

const str = (v: unknown) => (typeof v === "string" || typeof v === "number" ? String(v) : null);

/** Who an event is about: the subject's name, the payload's, a roster name, or a short id. */
export function eventNames(e: TeamEvent, names: Map<string, string>) {
  const p = (e.payload && typeof e.payload === "object" ? e.payload : {}) as Record<string, unknown>;
  const short = (id: string) => `${id.slice(0, 8)}…`;
  const subject = e.subject ? (e.subject.name ?? names.get(e.subject.userId) ?? str(p.name) ?? short(e.subject.userId)) : (str(p.name) ?? null);
  const actor = e.actor ?? (e.actorId ? (names.get(e.actorId) ?? (e.actorId === e.subject?.userId ? subject : null) ?? short(e.actorId)) : null);
  return { subject, actor };
}

/** How a player joined (backend/api: `code` | `link` | `match` | `created`; older rows use `joined`/`matched`). */
const VIA: Record<string, string> = { matched: " by Match me", match: " by Match me", code: " with the code", link: " with the link" };

const count = (v: unknown) => (Array.isArray(v) ? v.length : typeof v === "number" ? v : null);

/** One line for a team_events row (backend/api kinds, plus a few aliases). */
export function eventText(e: TeamEvent, names: Map<string, string> = new Map()): string {
  const p = (e.payload && typeof e.payload === "object" ? e.payload : {}) as Record<string, unknown>;
  const { subject, actor } = eventNames(e, names);
  // joined / left carry only the actor; removed and captain_changed name the target.
  const who = subject ?? (e.admin ? null : actor) ?? "a player";
  switch (e.kind) {
    case "created":
      return `Team created${str(p.name) ? ` as “${str(p.name)}”` : ""}${p.via === "matched" || p.via === "match" ? " by Match me" : ""}`;
    case "joined":
      return `${who} joined${VIA[str(p.via) ?? ""] ?? ""}`;
    case "left":
      return `${who} left`;
    case "removed":
      return `${who} was removed by the captain`;
    case "deleted":
      return `${who} deleted their account`;
    case "merged":
      return `${who}'s account was merged`;
    case "renamed":
      return `Renamed${str(p.from) ? ` from “${str(p.from)}”` : ""}${str(p.to) ? ` to “${str(p.to)}”` : ""}`;
    case "name_reset":
      return `Name reset${str(p.from) ? ` from “${str(p.from)}”` : ""}${str(p.to) ? ` to “${str(p.to)}”` : ""}`;
    case "captain_changed":
      return `${who} became captain`;
    case "updated": {
      const keys = Object.keys(p);
      return keys.length ? `Settings changed: ${keys.join(", ")}` : "Settings changed";
    }
    case "nudged":
      return `${actor ?? "A teammate"} nudged ${count(p.recipients) ?? "the"} teammate${count(p.recipients) === 1 ? "" : "s"}`;
    case "dormant":
      return `Went dormant${str(p.week) ? ` after ${str(p.week)}` : ""}: no finished sets for three weeks`;
    case "reactivated":
      return `Back in play${str(p.tier) ? ` in Division ${str(p.tier)}` : ""}`;
    case "disbanded": {
      const reason = str(p.reason)?.replace(/_/g, " ");
      return reason === "last member left" ? "Disbanded: the last member left" : reason === "admin" ? "Disbanded by an admin" : "Disbanded";
    }
    default:
      return e.kind.replace(/[_.]/g, " ");
  }
}
